import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IMetaData, TrendencySeoService } from '@trendency/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ComponentMapService } from 'src/app/shared/services/component-map.service';
import { BaseService } from '../../../../core/components/base/base.service';
import {
  IComponentData,
  IComponentFieldData,
  IGinopAgendaObject,
  IGinopExhibitorObject,
  IGinopImage,
} from '../../../../shared/shared.definitions';
import { GinopService } from '../../ginop.service';

@Component({
  selector: 'app-ginop-details',
  templateUrl: './ginop-details.component.html',
  styleUrls: ['./ginop-details.component.scss'],
})
export class GinopDetailsComponent implements OnInit, OnDestroy {
  url: string;
  title: string;
  image: IComponentFieldData | undefined;
  imageThumbnail: IComponentFieldData | undefined;
  country: string;
  city: string;
  subTitle: string;
  lat: string;
  long: string;
  date: Date;
  deadline: Date;
  information: string;
  venueInformation: string;
  parkingPossibilities: string;
  agendaList: Array<IGinopAgendaObject> = [];
  exhibitorList: Array<IGinopExhibitorObject> = [];
  isExhibitorRegActive: boolean;
  isParticipantRegActive: boolean;
  agendaSubtitle: string;
  thematicIcon: IComponentFieldData | undefined;

  private readonly destroy$ = new Subject();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly ginopService: GinopService,
    private readonly seo: TrendencySeoService,
    private readonly componentMapService: ComponentMapService,
    private readonly baseService: BaseService
  ) {}

  ngOnInit(): void {
    this.url = this.router.url;

    this.activatedRoute.data.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (res?.['data']?.data) {
        const meta = res?.['data']?.data?.meta;
        const components = res?.['data']?.data?.body;
        const eventContent = components.find((component: any) => component.type === 'Body.GinopEventContent');

        this.setValuesFromIComponentData(meta);
        this.setFieldsData(eventContent);
        this.setSubcomponentsData(eventContent);
        this.setMetaData(meta);

        this.baseService.setSz2020Logo(this.componentMapService.getComponentAsObjectByType(meta, 'Meta.GinopLogoStatus')?.ginopLogoStatus);
      }
    });
  }

  setValuesFromIComponentData(meta: IComponentData[]): void {
    for (const metaItem of meta) {
      if (metaItem?.fields?.length) {
        for (const field of metaItem.fields) {
          switch (field.key) {
            case 'city':
              this.city = field.value;
              break;
            case 'country':
              this.country = field.value;
              break;
            case 'deadline':
              this.deadline = field.value;
              break;
            case 'date':
              this.date = field.value;
              break;
            case 'isExhibitorRegActive':
              this.isExhibitorRegActive = field.value;
              break;
            case 'isParticipantRegActive':
              this.isParticipantRegActive = field.value;
              break;
            default:
          }
        }
      }
    }
  }

  setFieldsData(component: IComponentData): void {
    if (component.fields) {
      const locationPicker = JSON.parse(this.ginopService.getFieldValue(component.fields, 'locationPicker'));

      this.lat = locationPicker ? locationPicker.lat : null;
      this.long = locationPicker ? locationPicker.lng : null;

      this.image = component.fields.find((field) => field.key === 'image');
      this.imageThumbnail = component.fields.find((field) => field.key === 'imageThumbnail');
      this.thematicIcon = component.fields.find((field) => field.key === 'thematicIcon');
      this.title = this.ginopService.getFieldValue(component.fields, 'title');
      this.subTitle = this.ginopService.getFieldValue(component.fields, 'subTitle');
      this.information = this.ginopService.getFieldValue(component.fields, 'information');
      this.venueInformation = this.ginopService.getFieldValue(component.fields, 'venueInformation');
      this.parkingPossibilities = this.ginopService.getFieldValue(component.fields, 'parkingPossibilities');
      this.agendaSubtitle = this.ginopService.getFieldValue(component.fields, 'agendaSubTitle');
    }
  }

  setSubcomponentsData(component: IComponentData): void {
    if (component.subComponents) {
      for (const subComponent of component.subComponents) {
        if (subComponent.type === 'Body.AgendaItem') {
          const from = subComponent.fields.find((comp) => comp.key === 'from');
          const to = subComponent.fields.find((comp) => comp.key === 'to');
          const text = subComponent.fields.find((comp) => comp.key === 'text');

          if ((from && from.value) || (to && to.value) || (text && text.value)) {
            this.agendaList.push({
              from: from?.value,
              to: to?.value,
              text: text?.value,
            });
          }
        }

        if (subComponent.type === 'Body.ExhibitorItem') {
          const image = <IGinopImage>subComponent.fields.find((comp) => comp.key === 'image');
          const title = subComponent.fields.find((comp) => comp.key === 'title');
          const text = subComponent.fields.find((comp) => comp.key === 'text');

          if ((image && image.value) || (title && title.value) || (text && text.value)) {
            this.exhibitorList.push({
              image: image,
              title: title?.value,
              text: text?.value,
            });
          }
        }
      }
    }
  }

  setMetaData(meta: any): void {
    const metaTitle = this.componentMapService.getComponentAsObjectByType(meta, 'Meta.Title');
    const metaLead = this.componentMapService.getComponentAsObjectByType(meta, 'Meta.Lead');
    const metaThumbnail = this.componentMapService.getComponentAsObjectByType(meta, 'Meta.Thumbnail');
    const metaData: IMetaData = {
      title: metaTitle.text,
      description: metaLead.text,
      ogTitle: metaTitle.text,
      ogImage: metaThumbnail?.image?.url,
      ogImageAlt: metaThumbnail?.image?.image?.title,
      ogType: 'article',
    };
    this.seo.setMetaData(metaData);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
